import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Eye, Filter } from "lucide-react";
import { Select } from "antd";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISALS } from "../../constants/api";

import StatusPill, { STATUS_META } from "../../components/appraisal/StatusPill";
import useAppraisalRole from "../../hooks/useAppraisalRole";

const STATUS_OPTIONS = [
    "pending_hr_approval",
    "sent_back_by_hr",
    "hr_approved",
    "published",
    "overdue",
    "closed",
].map((s) => ({ value: s, label: STATUS_META[s]?.label || s.replace(/_/g, " ") }));

const FinalApprovalList = () => {
    const navigate = useNavigate();
    const { fetch } = useApiMethods();
    const { isHR } = useAppraisalRole();

    const [appraisals, setAppraisals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    const loadData = useCallback(async () => {
        if (!isHR) { setLoading(false); return; }
        setLoading(true);
        try {
            const filters = { scope: "all" };
            if (statusFilter) filters.status = statusFilter;
            const res = await fetch(APPRAISALS, filters);
            setAppraisals(res?.data || []);
        } catch (_) {
            ToastMsg("error", "Failed to load appraisals");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, isHR]);

    useEffect(() => { loadData(); }, [loadData]);

    if (!isHR) {
        return (
            <ContainerCard>
                <ModuleTitleCard Icon={CheckCircle2} title="Final Approval" subtitle="HR-only" />
                <div className="text-center py-12 text-muted-foreground">
                    You need HR permission to access this screen.
                </div>
            </ContainerCard>
        );
    }

    const filtered = appraisals.filter((a) => {
        if (!search) return true;
        return (a.employee?.name || "").toLowerCase().includes(search.toLowerCase());
    });

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={CheckCircle2}
                title="Final Approval"
                subtitle="HR verification, override, and publishing"
                totalRecords={filtered.length}
            />

            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mt-2">
                <div className="flex flex-wrap items-center gap-2 pb-3">
                    <div className="flex items-center gap-2 flex-1 min-w-[180px]">
                        <Filter className="h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search by employee"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="max-w-xs"
                        />
                    </div>
                    <Select
                        size="large"
                        placeholder="Status"
                        allowClear
                        value={statusFilter || undefined}
                        onChange={(v) => setStatusFilter(v || "")}
                        options={STATUS_OPTIONS}
                        style={{ minWidth: 200 }}
                    />
                </div>

                <div className="overflow-auto">
                    {loading ? (
                        <div className="text-center py-10 text-muted-foreground">Loading…</div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-10 text-muted-foreground">No appraisals found.</div>
                    ) : (
                        <div className="overflow-x-auto rounded-md border border-border">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/40">
                                    <tr className="text-left text-muted-foreground">
                                        <th className="px-3 py-2 font-medium">Employee</th>
                                        <th className="px-3 py-2 font-medium hidden sm:table-cell">RM</th>
                                        <th className="px-3 py-2 font-medium hidden md:table-cell">Cycle</th>
                                        <th className="px-3 py-2 font-medium hidden lg:table-cell">RM Reviewed</th>
                                        <th className="px-3 py-2 font-medium">Status</th>
                                        <th className="px-3 py-2 font-medium text-right">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((a) => (
                                        <tr key={a.id} className="border-t border-border hover:bg-muted/20">
                                            <td className="px-3 py-2">
                                                <button
                                                    className="font-medium hover:text-sidebar-primary text-left"
                                                    onClick={() => navigate(`/appraisal/final-approval/${a.id}`)}
                                                >
                                                    {a.employee?.name || "—"}
                                                </button>
                                                <p className="text-xs text-muted-foreground">{a.employee?.designation}</p>
                                            </td>
                                            <td className="px-3 py-2 hidden sm:table-cell">{a.reporting_manager?.name || "—"}</td>
                                            <td className="px-3 py-2 hidden md:table-cell">{a.cycle_name || "—"}</td>
                                            <td className="px-3 py-2 hidden lg:table-cell text-xs text-muted-foreground">
                                                {a.rm_reviewed_at ? dayjs(a.rm_reviewed_at).format("DD MMM YYYY") : "—"}
                                            </td>
                                            <td className="px-3 py-2"><StatusPill value={a.status} /></td>
                                            <td className="px-3 py-2 text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => navigate(`/appraisal/final-approval/${a.id}`)}
                                                >
                                                    <Eye className="w-4 h-4 mr-1" /> Open
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </ContainerCard>
    );
};

export default FinalApprovalList;
